import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fmtShort } from '@quadro/domain';
import { hud } from '../../components/Hud';
import * as api from '../../data/api';
import { useAction } from '../../data/queries';

const KIND: Record<api.AutomationKind, string> = { email: 'Enviar e-mail', fund_report: 'Enviar o relatório dos fundos' };
const VARS = ['data', 'mes', 'mes_anterior', 'ano', 'rotina', 'responsavel', 'remetente', 'link'];
const DEFAULTS: Record<api.AutomationKind, Pick<api.AutomationConfig, 'subject' | 'body' | 'intro'>> = {
  email: { subject: 'Lembrete: {{rotina}} ({{mes}})', body: 'Olá,\n\nLembrete da rotina "{{rotina}}" de {{data}}.\n\nObrigado,\n{{remetente}}' },
  fund_report: { subject: 'Report dos fundos: {{data}}', intro: 'Bom dia,\n\nSegue o report dos fundos com data-base mais recente do warehouse.' },
};

interface Draft { id: string | null; kind: api.AutomationKind; runAt: string; to: string; cc: string; subject: string; text: string; active: boolean }
const emails = (s: string) => s.split(/[\s,;]+/).map(x => x.trim()).filter(Boolean);
const toDraft = (a: api.Automation): Draft => ({
  id: a.id, kind: a.kind, runAt: a.runAt, to: a.config.to.join(', '), cc: (a.config.cc ?? []).join(', '),
  subject: a.config.subject, text: (a.kind === 'email' ? a.config.body : a.config.intro) ?? '', active: a.active,
});
const fresh = (kind: api.AutomationKind): Draft => ({
  id: null, kind, runAt: kind === 'email' ? '09:00' : '08:00', to: '', cc: '', subject: DEFAULTS[kind].subject,
  text: (kind === 'email' ? DEFAULTS.email.body : DEFAULTS.fund_report.intro) ?? '', active: true,
});
const config = (d: Draft): api.AutomationConfig => ({
  to: emails(d.to), cc: emails(d.cc), subject: d.subject, ...(d.kind === 'email' ? { body: d.text } : { intro: d.text }),
});

/** Última execução, em uma linha: o que aconteceu e quando. */
function lastRun(a: api.Automation) {
  const r = a.runs[0];
  if (!r) return { cls: '', text: 'Ainda não executou' };
  const when = `${fmtShort(r.at.slice(0, 10))} às ${new Date(r.at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  if (r.status === 'sent') return { cls: 'ok', text: `Enviado ${when} para ${r.recipients.length} pessoa${r.recipients.length === 1 ? '' : 's'}` };
  if (r.status === 'failed') return { cls: 'bad', text: `Falhou ${when}: ${r.detail ?? ''}` };
  if (r.status === 'test') return { cls: '', text: `${r.detail?.startsWith('Teste falhou') ? r.detail : `Teste enviado ${when}`}` };
  return { cls: '', text: `Enviando (${when})` };
}

/**
 * Automações da rotina: em cada ocorrência, no horário, o Quadro executa a ação.
 * O e-mail sai da caixa de quem salvou a automação por último.
 */
export function Automations({ routineId, canWrite }: { routineId: string; canWrite: boolean }) {
  const key = ['automations', routineId] as const;
  const list = useQuery({ queryKey: key, queryFn: () => api.fetchAutomations(routineId) });
  const features = useQuery({ queryKey: ['features'], queryFn: api.fetchFeatures, staleTime: Infinity });
  const save = useAction(api.saveAutomation, [key]);
  const remove = useAction(api.deleteAutomation, [key]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirm, setConfirm] = useState<string | null>(null);

  async function test(a: api.Automation) {
    try {
      const r = await api.testAutomation(a.id);
      hud(`Teste enviado para ${r.sentTo}`);
    } catch (e) { hud((e as Error).message, 'info'); }
    void list.refetch();
  }

  return (
    <section className="au" aria-label="Automações">
      <h3>Automações</h3>
      <p className="muted au-help">Em cada ocorrência desta rotina, no horário escolhido, o Quadro executa a ação. O e-mail sai da caixa de quem salvou a automação.</p>
      {features.data && !features.data.mail && (
        <p className="au-note">O envio de e-mails está desligado no servidor. As automações ficam salvas e passam a enviar quando o administrador do sistema ligar o envio.</p>
      )}
      {list.data?.map(a => {
        const run = lastRun(a);
        return draft?.id === a.id ? <Editor key={a.id} routineId={routineId} draft={draft} onChange={setDraft} onCancel={() => setDraft(null)}
          onSave={async d => { await save({ id: d.id, routineId, kind: d.kind, config: config(d), runAt: d.runAt, active: d.active }); setDraft(null); hud('Automação salva'); }} />
          : (
            <div className={`au-item ${a.active ? '' : 'off'}`} key={a.id}>
              <div className="au-main">
                <b>{KIND[a.kind]}</b>
                <span className="muted">às {a.runAt} · para {a.config.to.join(', ')}{a.config.cc?.length ? ` · cc ${a.config.cc.length}` : ''}{a.active ? '' : ' · pausada'}</span>
                <span className="muted">Assunto: {a.config.subject}</span>
                <span className={`au-run ${run.cls}`}>{run.text}</span>
              </div>
              {canWrite && (
                <div className="au-acts">
                  <button className="btn" onClick={() => setDraft(toDraft(a))}>Editar</button>
                  <button className="btn" onClick={() => test(a)} title="Envia só para você, agora">Testar agora</button>
                  <button className="btn" onClick={() => save({ id: a.id, routineId, kind: a.kind, config: a.config, runAt: a.runAt, active: !a.active })}>{a.active ? 'Pausar' : 'Ativar'}</button>
                  {confirm === a.id
                    ? <button className="btn danger" onClick={() => { setConfirm(null); void remove(a.id); }}>Confirmar exclusão</button>
                    : <button className="btn" onClick={() => setConfirm(a.id)}>Excluir</button>}
                </div>
              )}
            </div>
          );
      })}
      {list.data && !list.data.length && !draft && <p className="muted">Nenhuma automação nesta rotina.</p>}
      {draft && !draft.id && <Editor routineId={routineId} draft={draft} onChange={setDraft} onCancel={() => setDraft(null)}
        onSave={async d => { await save({ id: null, routineId, kind: d.kind, config: config(d), runAt: d.runAt, active: d.active }); setDraft(null); hud('Automação criada'); }} />}
      {canWrite && !draft && (
        <div className="au-add">
          <button className="btn" onClick={() => setDraft(fresh('email'))}>+ E-mail</button>
          <button className="btn" onClick={() => setDraft(fresh('fund_report'))}>+ Relatório dos fundos</button>
        </div>
      )}
    </section>
  );
}

function Editor({ routineId, draft: d, onChange, onSave, onCancel }: {
  routineId: string; draft: Draft; onChange: (d: Draft) => void; onSave: (d: Draft) => Promise<void>; onCancel: () => void;
}) {
  const [preview, setPreview] = useState<{ subject: string; html: string; to: string[]; cc: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const text = useRef<HTMLTextAreaElement>(null);
  const patch = (p: Partial<Draft>) => { onChange({ ...d, ...p }); setPreview(null); };

  function insert(v: string) {
    const el = text.current, token = `{{${v}}}`;
    if (!el) return patch({ text: d.text + token });
    const s = el.selectionStart ?? d.text.length, e = el.selectionEnd ?? s;
    patch({ text: d.text.slice(0, s) + token + d.text.slice(e) });
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(s + token.length, s + token.length); });
  }
  async function run(fn: () => Promise<void>) {
    setBusy(true); setError(null);
    try { await fn(); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <div className="au-edit">
      <div className="form">
        <label htmlFor="auTipo">Ação</label>
        <div className="seg" id="auTipo" role="group" aria-label="Tipo de automação">
          {(Object.keys(KIND) as api.AutomationKind[]).map(k => (
            <button key={k} type="button" aria-pressed={d.kind === k} disabled={!!d.id && d.kind !== k}
              onClick={() => d.kind !== k && onChange({ ...fresh(k), id: d.id, to: d.to, cc: d.cc, runAt: d.runAt })}>{k === 'email' ? 'E-mail' : 'Relatório dos fundos'}</button>
          ))}
        </div>
        <label htmlFor="auHora">Horário</label>
        <input id="auHora" type="time" value={d.runAt} onChange={e => patch({ runAt: e.target.value })} />
        <label htmlFor="auPara">Para</label>
        <input id="auPara" value={d.to} onChange={e => patch({ to: e.target.value })} placeholder="e-mails separados por vírgula" />
        <label htmlFor="auCc">Cc</label>
        <input id="auCc" value={d.cc} onChange={e => patch({ cc: e.target.value })} placeholder="opcional" />
        <label htmlFor="auAssunto">Assunto</label>
        <input id="auAssunto" maxLength={200} value={d.subject} onChange={e => patch({ subject: e.target.value })} />
        <label htmlFor="auTexto">{d.kind === 'email' ? 'Mensagem' : 'Introdução'}</label>
        <textarea id="auTexto" ref={text} rows={d.kind === 'email' ? 6 : 3} value={d.text} onChange={e => patch({ text: e.target.value })} />
      </div>
      <div className="au-vars"><span className="muted">Inserir:</span>{VARS.map(v => <button key={v} type="button" className="chip" onClick={() => insert(v)}>{`{{${v}}}`}</button>)}</div>
      {d.kind === 'fund_report' && <p className="muted au-help">O relatório traz, por fundo, o PL mais recente do warehouse, a variação, a exposição final e as tarefas ligadas no Quadro. Só pode ir para e-mails da empresa.</p>}
      {error && <p className="auth-error" role="alert">{error}</p>}
      {preview && (
        <div className="au-preview">
          <span className="muted">Para {preview.to.join(', ')}{preview.cc.length ? ` · cc ${preview.cc.join(', ')}` : ''}</span>
          <b>{preview.subject}</b>
          <iframe title="Prévia do e-mail" sandbox="" srcDoc={preview.html} />
        </div>
      )}
      <div className="au-acts">
        <label className="switch"><input type="checkbox" checked={d.active} onChange={e => patch({ active: e.target.checked })} /> Ativa</label>
        <span className="grow" />
        <button className="btn" onClick={onCancel}>Cancelar</button>
        <button className="btn" disabled={busy} onClick={() => run(async () => setPreview(await api.previewAutomation({ routineId, kind: d.kind, config: config(d) })))}>Ver prévia</button>
        <button className="btn primary" disabled={busy || !emails(d.to).length || !d.subject.trim()} onClick={() => run(() => onSave(d))}>Salvar automação</button>
      </div>
    </div>
  );
}
